import React, { useState } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { ArrowRight, CheckCircle, LockKey, EnvelopeSimple, PhoneCall } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAILJS_SERVICE_ID = "service_jvaopxc";
const EMAILJS_TEMPLATE_ID = "template_8kfwwhe";
const EMAILJS_PUBLIC_KEY = "5MdsvUkN7ABTQfSBN";

export default function QuoteBlock({ configuratorData }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", location: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("A név és a telefonszám kötelező.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, ...(configuratorData || {}) };
      const { data: saved } = await axios.post(`${API}/quote`, payload);

      // Send email via EmailJS (best-effort, doesn't block UX if it fails)
      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            name: saved.name || "",
            phone: saved.phone || "",
            email: saved.email || "",
            location: saved.location || "",
            message: saved.message || "",
            width: saved.width ?? "",
            length: saved.length ?? "",
            height: saved.height ?? "",
            panel_thickness: saved.panel_thickness || "",
            color: saved.color || "",
            trim_color: saved.trim_color || "",
            door_type: saved.door_type || "",
            door_breakdown: saved.door_breakdown || "",
            roof_type: saved.roof_type || "",
            passage_doors: saved.passage_doors ?? 0,
            windows: saved.windows ?? 0,
            estimated_price: saved.estimated_price
              ? Number(saved.estimated_price).toLocaleString("hu-HU")
              : "",
            id: saved.id || "",
            created_at: saved.created_at || new Date().toISOString(),
          },
          { publicKey: EMAILJS_PUBLIC_KEY }
        );
      } catch (emailErr) {
        console.warn("EmailJS error:", emailErr);
      }

      setSent(true);
      toast.success("Ajánlatkérés elküldve — hamarosan keressük!");
    } catch (err) {
      toast.error("Hiba történt. Próbálja újra vagy hívjon minket.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="py-20 md:py-32 bg-[#F5F5F5]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-0 border border-[#E4E4E7]">
          <div className="p-8 md:p-12 bg-[#0F0F0F] text-[#FFFFFF] relative overflow-hidden">
            <div className="absolute -top-10 -right-10 watermark text-[200px] select-none pointer-events-none">R</div>
            <div className="relative">
              <span className="badge-chip" style={{ background: "#FFFFFF", color: "#0F0F0F" }}>
                Ajánlatkérés
              </span>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-4 leading-[0.95] mb-6">
                Kapjon fix árat<br/>24 órán belül.
              </h2>
              <p className="text-[#E4E4E7] text-base md:text-lg leading-relaxed mb-8">
                Töltse ki az űrlapot. Kollégánk visszahívja, átbeszéljük a részleteket, és e-mailben küldjük a fix vállalási árat.
              </p>

              <ul className="space-y-4 mb-8">
                {[
                  "Ingyenes, nem kötelező érvényű ajánlat",
                  "Pontos, tételes árkalkuláció",
                  "Javaslat a kivitelezési ütemre (dátumokkal)",
                  "Válasz 24 munkaórán belül",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle size={22} weight="fill" className="text-[#FFFFFF] shrink-0 mt-0.5" />
                    <span className="text-[#FFFFFF]">{b}</span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-2 text-sm text-[#E4E4E7] pt-6 border-t border-[#525252]/40">
                <LockKey size={16} />
                <span>Nem spamelünk, nem adjuk ki harmadik félnek.</span>
              </div>
            </div>
          </div>

          <div className="p-8 md:p-12 bg-[#FFFFFF]">
            {sent ? (
              <div data-testid="quote-success" className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 bg-[#0F0F0F] flex items-center justify-center rounded-sm mb-4">
                  <CheckCircle size={32} weight="fill" className="text-[#FFFFFF]" />
                </div>
                <h3 className="font-display font-black text-3xl tracking-tight mb-2">Köszönjük!</h3>
                <p className="text-[#525252] max-w-md mb-6">
                  Megkaptuk az igényét. Kollégánk <strong>24 munkaórán belül</strong> visszahívja, és elküldjük az árajánlatot e-mailben is.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-4 text-sm text-[#525252]">
                  <div className="flex items-center gap-2">
                    <EnvelopeSimple size={18} />
                    <span>Visszaigazolást küldtünk</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PhoneCall size={18} />
                    <span>Hamarosan csörög a telefon</span>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form">
                <h3 className="font-display font-black text-2xl md:text-3xl tracking-tight mb-2">
                  Ajánlatkérés
                </h3>
                <p className="text-[#525252] text-sm mb-6">
                  Gyors kitöltés. Csak a legszükségesebb mezők kötelezőek.
                </p>

                {configuratorData && (
                  <div className="mb-6 p-3 bg-[#F5F5F5] border border-[#FFFFFF] rounded-sm text-sm" data-testid="quote-config-loaded">
                    <div className="flex items-center gap-2 font-semibold text-[#0F0F0F] mb-1">
                      <CheckCircle size={16} weight="fill" /> Konfigurátor adatok betöltve
                    </div>
                    <div className="text-[#525252] text-xs">
                      {configuratorData.width}×{configuratorData.length}×{configuratorData.height}m · {configuratorData.panel_thickness}mm panel · Panel: {configuratorData.color} / Szegő: {configuratorData.trim_color} · Kapuk: {configuratorData.door_breakdown || "—"} · Ajtó {configuratorData.passage_doors || 0} / Ablak {configuratorData.windows || 0} db · becsült: {configuratorData.estimated_price?.toLocaleString("hu-HU")} Ft
                    </div>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <Field label="Név*" testid="quote-name">
                    <input className="input-sharp" required value={form.name} onChange={onChange("name")} data-testid="quote-name-input" />
                  </Field>
                  <Field label="Telefon*" testid="quote-phone">
                    <input className="input-sharp" required type="tel" placeholder="+36 20 ..." value={form.phone} onChange={onChange("phone")} data-testid="quote-phone-input" />
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <Field label="E-mail" testid="quote-email">
                    <input className="input-sharp" type="email" value={form.email} onChange={onChange("email")} data-testid="quote-email-input" />
                  </Field>
                  <Field label="Helyszín (város / megye)" testid="quote-location">
                    <input className="input-sharp" value={form.location} onChange={onChange("location")} data-testid="quote-location-input" />
                  </Field>
                </div>

                <Field label="Egyéb igény (opcionális)" testid="quote-message">
                  <textarea
                    className="input-sharp"
                    rows={3}
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder="Pl. szigetelés, villany, ablak, időzítés..."
                    data-testid="quote-message-input"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quote-submit"
                  className="cta-primary w-full justify-center mt-6 disabled:opacity-60"
                >
                  {loading ? "Küldés..." : "Ajánlatot kérek"} <ArrowRight size={18} weight="bold" />
                </button>

                <p className="text-xs text-[#525252] mt-3 text-center">
                  Az elküldéssel elfogadja az <a href="#" className="underline">adatkezelési tájékoztatót</a>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={`field-${testid}`}>
      <span className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
